import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Banner from '@cpg-its/ads.containers.banner';
import { Form, Formik, FormikProps } from 'formik';
import { DividerSection } from '../../../global/components/layout/divider-section';
import { TcsmStates } from '../../../root/tcsm-model';
import { ClergyBioSettings } from '../../arc/models/arc-epi-props';
import { ClergyBioChurchEducation, DataState } from '../model/clergy-bio-app-models';
import ClergyBioComponentLoader from '../components/clergy-bio-component-loader';
import { LabelSettingProps } from '../../../global/models/epi-model';
import { ApiStatuses } from '../../../global/models/api-model';
import { ClergyBioEducationFormModel } from './models/form-models';
import { setClergyBioEducation, setLoadingState } from '../clergy-bio-slice';
import { getClergyBioEducation, patchClergyBioEducation } from '../../../global/ducks/clergy-bio-api';
import { ConstructPayload, GetClergyBioEducationValidationSchema, getNewEducation, updateEducation } from './clergy-bio-education-helpers';
import { FormWrapper } from '../../../global/components/form-wrapper';
import { FormTypes } from '../../account-info/models/form-models';
import EcdMessage from '../summary/ecd-message';
import { ContentSpacer } from '../../../global/components/layout/generic-content-spacer';
import ClergyBioEducationItem from './clergy-bio-education-item';
import { buttonStyles } from '../../../global/constants/theme/styles/components/buttonstyles';
import { PageHeader } from '../../../global/components/layout/page-header';
import { ClergyBioGaFormTypes, HandleClergyBioSubmit } from '../../../global/helpers/analytics-helper';
import { apiErrorClear } from '../../../global/ducks/base-api';



function ClergyBioEducationUpdate() {

	const buttonClasses = buttonStyles();
	const settings = useSelector<TcsmStates, ClergyBioSettings>(state => state?.epiProps?.clergyBioSettingsState?.epiContent);
	const loadingState = useSelector<TcsmStates, DataState>(state => state?.clergyBioState?.loaded?.education);
	const education = useSelector<TcsmStates, ClergyBioChurchEducation>(state => state?.clergyBioState?.education);
	const guid = useSelector<TcsmStates, string>(state => state?.authenticatedState?.clientGuid);
	const labelSettings = useSelector<TcsmStates, LabelSettingProps>(state => state?.epiProps?.labelSettingState?.epiContent);
	const dispatch = useDispatch();
	const [updateStatus, setUpdateStatus] = useState<ApiStatuses>(ApiStatuses.None);
	const [errorMessage, setErrorMessage] = useState<string>(null);
	const [errorMapped, setErrorMapped] = useState<any>(null);
	const defaultValues = {
		theological: education?.theological?.details?.map(a => updateEducation(a)),
		other: education?.other?.details?.map(a => updateEducation(a)),
	};
	const [initialValues, setInitialValues] = useState<ClergyBioEducationFormModel>(
		{
			theological: [],
			other: [],
		},
	);

	const onError = (res) => {

		if (res?.status?.code === 'ERROR') {
			setUpdateStatus(ApiStatuses.Failure);
			setErrorMessage(res?.status?.message);
			const errors: any = {};
			errors.theological = res?.data?.theological?.details?.map(item => { return { year: item?.errors?.year && item?.errors?.year[0] }; });
			errors.other = res?.data?.other?.details?.map(item => { return { year: item?.errors?.graduation_year && item?.errors?.graduation_year[0] }; });
			setErrorMapped(errors);

		}

	};
	const onSuccess = (res) => {
		if (res?.status?.code === 'SUCCESS') {
			HandleClergyBioSubmit(ClergyBioGaFormTypes.Education, guid);
			setUpdateStatus(ApiStatuses.Success);
			dispatch(setClergyBioEducation(res?.data));

		}
		if (res?.status?.code === 'ERROR') {
			setUpdateStatus(ApiStatuses.Failure);
			setErrorMessage(res?.status?.message);
			const errors: any = {};
			errors.theological = res?.data?.theological?.details?.map(item => { return { year: item?.errors?.year && item?.errors?.year[0] }; });
			errors.other = res?.data?.other?.details?.map(item => { return { year: item?.errors?.graduation_year && item?.errors?.graduation_year[0] }; });
			setErrorMapped(errors);

		}

	};

	const updateEducations = (values: ClergyBioEducationFormModel) => {
		setErrorMapped(null);
		dispatch(apiErrorClear());
		dispatch(patchClergyBioEducation(guid, ConstructPayload(values, defaultValues), onSuccess, onError));

	};
	const validationSchema = GetClergyBioEducationValidationSchema(settings, labelSettings);

	const addNewEducation = (key: keyof ClergyBioEducationFormModel) => {
		const updated = initialValues[key];
		updated.push(getNewEducation());
		const copy = { ...initialValues };
		copy[key] = updated;
		setInitialValues(copy);
	};

	useEffect(() => {

		setInitialValues(
			{
				theological: education?.theological?.details.map(a => updateEducation(a)),
				other: education?.other?.details.map(a => updateEducation(a)),
			},
		);

	}, [education]);

	useEffect(() => {

		return function cleanUp() {
			dispatch(setLoadingState(DataState.LOADING, 'education'));
			dispatch(getClergyBioEducation(
				guid,
				(data) => dispatch(setClergyBioEducation(data?.data)),
				() => dispatch(setLoadingState(DataState.ERROR, 'education'))),
			);
		};
	}, []);

	function renderContent() {
		return (
			<Formik
				initialValues={initialValues}
				onSubmit={(values) => updateEducations(values)}
				validationSchema={validationSchema}
				enableReinitialize
				initialErrors={errorMapped}
			>
				{(formProps: FormikProps<ClergyBioEducationFormModel>) => (

					<Form>
						{
							updateStatus === ApiStatuses.Failure &&
							<ContentSpacer>
								<Banner
									banner={{
										type: 'error',
										label: <p><strong>{errorMessage}</strong></p>,
									}}
								/>
							</ContentSpacer>
						}
						<FormWrapper
							formikProps={formProps}
							submitText={labelSettings?.submitText}
							cancelText={labelSettings?.cancelButtonText}
							onCancel={() => {
								window.location.reload();
							}}
							initialValue={defaultValues}
							successfulForms={updateStatus === ApiStatuses.Success ? [FormTypes.CLERGY_BIO_EDUCATION_UPDATE] : []}
						>
							<PageHeader
								headerText={settings?.educationTitle}
								description={settings?.educationUpdateAboutSectionText}
							/>
							<ContentSpacer>
								<EcdMessage />
							</ContentSpacer>
							<DividerSection sectionText={settings?.theologicalEducationTitle} showCleryIcon>
								{
									initialValues?.theological?.map((a, idx) => {
										return <ClergyBioEducationItem
											key={`${a?.id || 'theological'}_${idx}`}
											arrayField='theological'
											id={idx?.toString()}
											settings={settings}
											formikProps={formProps}
											setList={setInitialValues}
										/>;
									})
								}
								<button className={buttonClasses.secondaryButton} type='button' onClick={() => addNewEducation('theological')} >{settings?.addTheologicalInstitutionButtonText}</button>

							</DividerSection>
							<DividerSection removeDivider sectionText={settings?.otherEducationTitle} showCleryIcon >
								{
									initialValues?.other?.map((a, idx) => {
										return <ClergyBioEducationItem
											key={`${a?.id || 'other'}_${idx}`}
											arrayField='other'
											id={idx?.toString()}
											settings={settings}
											formikProps={formProps}
											isOtherEducation
											setList={setInitialValues}
										/>;
									})
								}
								<button className={buttonClasses.secondaryButton} type='button' onClick={() => addNewEducation('other')} >{settings?.addOtherInstitutionButtonText}</button>
							</DividerSection>
						</FormWrapper>
					</Form>

				)}
			</Formik>
		);
	}

	return (
		<ClergyBioComponentLoader
			state={loadingState}
			content={renderContent()}
		/>
	);
}


export default ClergyBioEducationUpdate;